import React, { useContext, useState } from 'react'
import SvgSurface from '../../../icons/SvgSurface'
import { FormattedMessage } from 'react-intl'
import thousandsSeparator from '../../../utils/formatter/thousandsSeparator'
import SvgPlusSansCercle from '../../../icons/SvgPlusSansCercle'
import { fontFamilyBlackerDisplay, fontFamilyMonserrat, WHITE } from '../../../theme'
import { AutresElementsCrossSellingModel } from '../types/autresElementsCrossSellingModel'
import styles from '../../../styles'
import Link from '../../link/Link'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { makeStyles } from 'tss-react/mui'
import { Grid, useTheme } from '@mui/material'
import { useNavigate } from 'react-router'

const useStyles = makeStyles()((theme) => ({
		crossSellingCardsContentContainer: {
			cursor: 'pointer',
			width: '100%',

			'& > div': {
				width: '100%'
			}
		},
		surTitreVignette: {
			color: theme.palette.primary.main,
			fontFamily: fontFamilyMonserrat,
			fontSize: 11,
			fontWeight: 500,
			marginBottom: 10,
			marginTop: 0,
			padding: 0,
			background: 0,
			borderRadius: 0,
			width: 'fit-content'
		},
		titresAutreResidences: {
			fontFamily: fontFamilyBlackerDisplay,
			fontWeight: 900,
			fontSize: 18,
			display: 'flex',
			flexDirection: 'column',
			marginTop: '0.83em'
		},
		surTitreAutreResidence: {
			color: theme.palette.blue.main,
			fontFamily: fontFamilyMonserrat,
			fontWeight: 600,
			fontSize: 18,
			margin: 0,

			'&:first-letter': {
				textTransform: 'capitalize'
			}
		},
		intituleCRMAutreResidence: {
			color: theme.palette.primary.main,
			minHeight: 20,
			fontFamily: fontFamilyMonserrat,
			fontWeight: 600,
			fontSize: 18,
			margin: 0
		},
		voirFiche: {
			fontSize: 13,
			fontFamily: fontFamilyMonserrat,
			fontWeight: 600
		},
		voirFicheLink: {
			color: theme.palette.blue.main,
			textDecoration: 'none',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between'
		},
		plusIcon: {
			height: 32,
			width: 32,
			borderRadius: 30
		},
		plusIconNotHover: {
			background: theme.palette.blue[20]
		},
		plusIconHover: {
			background: theme.palette.blue.main
		},
		plusIconContainer: {
			justifyContent: 'flex-end'
		},
		lieuOffre: {
			...styles(theme).text6,
			color: theme.palette.primary.main,
			marginTop: 4,
			marginBottom: 0
		},
		atoutsContainer: {
			marginTop: 7,
			marginBottom: 13
		},
		atoutContainer: {
			'& img, & span, & svg': {
				verticalAlign: 'middle'
			},
			'& img': {
				objectFit: 'contain'
			}
		},
		atoutTexte: {
			...styles(theme).text6,
			lineHeight: '14px',
			margin: 0,
			marginLeft: 5,
			whiteSpace: 'nowrap'
		},
		atoutTexteContainer: {
			textOverflow: 'ellipsis',
			overflow: 'hidden'
		},
		prixLoyer: {
			...styles(theme).title6,
			fontSize: 18,

			'& sup': {
				fontSize: 8,
				fontWeight: 500,
				fontFamily: fontFamilyMonserrat
			}
		},
		autresAtouts: {
			width: '100%',
			display: 'flex',
			marginTop: 10
		}
	})
)

interface AcceptingProps {
	elementCrossSelling: AutresElementsCrossSellingModel
}

type CrossSellingCardsContentProps = AcceptingProps

const CrossSellingCardsContent: React.FC<CrossSellingCardsContentProps> = (
	{
		elementCrossSelling
	}
) => {
	const theme = useTheme()
	const navigate = useNavigate()
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles()
	const [bottomHover, setbottomHover] = useState<boolean>(false)
	const affichageSpecifiqueDesAtouts = 'depuisLaCarte' in elementCrossSelling && elementCrossSelling.depuisLaCarte && !isLargeScreen

	const plusClass = cx(
		classes.plusIcon,
		!bottomHover && isLargeScreen ? classes.plusIconNotHover : classes.plusIconHover
	)

	return (
		<Link
			className={classes.crossSellingCardsContentContainer}
			href={elementCrossSelling.alias}
			onClick={() => navigate(elementCrossSelling.alias)}
			noAnimation
			hasChevron={false}
			prevent
		>
			<div
				onMouseEnter={() => setbottomHover(true)}
				onMouseLeave={() => setbottomHover(false)}
			>
				{
					// S'il y a un surTitre de la vignette (présent sur la page du détail d'une résidence)
					// elementCrossSelling.surTitreVignette && router.pathname !== linksEnum.nosResidences && (
					elementCrossSelling.surTitreVignette && (
						<span className={classes.surTitreVignette}>{elementCrossSelling.surTitreVignette}</span>
					)
				}

				<h2 className={classes.titresAutreResidences}>
					<span className={classes.surTitreAutreResidence}>{elementCrossSelling.surTitre}</span>

					{
						// S'il y a un intituleCRM (présent sur la page du détail d'une résidence)
						elementCrossSelling.intituleCRM && (
							<span className={classes.intituleCRMAutreResidence}>{elementCrossSelling.intituleCRM}</span>
						)
					}

					{
						// S'il y a une indication sur le lieu de l'offre (présent sur la page du détail de l'offre ou la page d'accueil)
						elementCrossSelling.lieuOffre && (
							<span className={classes.lieuOffre}>{elementCrossSelling.lieuOffre}</span>
						)
					}
				</h2>

				{
					// Listing des atouts si définis (présent sur la page du détail de l'offre)
					elementCrossSelling.atouts && (
						<Grid container item alignItems="center" justifyContent="flex-start" className={classes.atoutsContainer} spacing={1}>
							<Grid container item xs={affichageSpecifiqueDesAtouts ? 'auto' : 6} alignItems="center" className={classes.atoutContainer}>
								<SvgSurface width={17} height={17} color={theme.palette.primary.main} />
								<Grid container item xs minWidth={0}>
									<div className={classes.atoutTexteContainer}>
										<span className={classes.atoutTexte}>
									{elementCrossSelling.surface ?? ''}
											<FormattedMessage
												id="global.surface"
												defaultMessage=" m²"
												description="Atout surface"
											/>
									</span>
									</div>
								</Grid>
							</Grid>

							{
								elementCrossSelling.atouts.length > 0 && (
									elementCrossSelling.atouts.slice(0, affichageSpecifiqueDesAtouts ? 1 : 3).map(
										(atout, index) => (
											<Grid
												container
												item
												xs={affichageSpecifiqueDesAtouts ? 'auto' : 6}
												alignItems="center"
												key={`atout-${elementCrossSelling.surTitre}-${atout.texte}-${index}`}
												className={classes.atoutContainer}
											>
												<img src={atout.icone} alt={atout.texte} width={17} height={17} />
												<Grid container item xs minWidth={0}>
													<div className={classes.atoutTexteContainer}>
														<span className={classes.atoutTexte}>{atout.texte}</span>
													</div>
												</Grid>
											</Grid>
										)
									)
								)
							}
						</Grid>
					)
				}

				<Grid container item className={classes.voirFiche}>
					<Grid
						item
						container
						className={classes.voirFicheLink}
					>
						<Grid item xs={7}>
							{
								// S'il y a une tarification, on affiche le prix (présent sur la page du détail de l'offre)
								elementCrossSelling.tarification ? (
									<span className={classes.prixLoyer}>
										<FormattedMessage
											id="offre.offresLiees.tarification.loyer"
											defaultMessage="{loyer} € <sup>CC</sup>"
											description="Montant du loyer en €"
											values={{
												loyer: thousandsSeparator(elementCrossSelling.tarification, false),
												sup: (chunk) => <sup>{chunk}</sup>
											}}
										/>
									</span>
								) : (
									<FormattedMessage
										id="residence.autreResidence.voirFiche"
										defaultMessage="Voir la fiche"
										description="Lot type from step4 text"
									/>
								)
							}
						</Grid>
						<Grid item container xs className={classes.plusIconContainer}>
							<Grid item className={plusClass}>
								<SvgPlusSansCercle color={WHITE} width={32} height={32} />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</div>
		</Link>
	)
}

export default CrossSellingCardsContent